/**
 * Agent hotel search results — single SSOT for normalize / apply / reset filters.
 * Used by searchresults.jsx (HOTELS engine path) and Dynamic Packages via
 * applyDynamicPackageFilters (API list price as _filterPrice — no package reprice).
 */
import { agentHotelSearchLowestPackagePrice } from "../../utils/agentHotelSearchApiPrice";
import { computeAgentHotelSearchResultPriceVm } from "../../utils/agentHotelSearchResultsPricing";
import { resolveCampaignHotelSearchDisplayPrice } from "../../utils/injectDestinationCampaignHotels";

export const HOTEL_PRICE_PRESET = {
  ALL: "all",
  LOW: "low",
  MID: "mid",
  HIGH: "high",
  CUSTOM: "custom",
};

/** Top-bar price dropdown + side-modal price sort radios (SSOT). */
export const HOTEL_PRICE_SORT_FILTER = {
  ALL: "all",
  LOW_TO_HIGH: "low_to_high",
  HIGH_TO_LOW: "high_to_low",
};

export const HOTEL_SORT_FIELD = {
  STARS: "stars",
  PRICE: "price",
};

export const HOTEL_SORT_DIRECTION = {
  ASC: "asc",
  DESC: "desc",
};

export const HOTEL_FILTER_KEYS = {
  NAME: "name",
  STAR_RATING: "starRating",
  BOARD_BASIS: "boardBasis",
  AREAS: "areas",
  PRICE_PRESET: "pricePreset",
  PRICE_MIN: "priceMin",
  PRICE_MAX: "priceMax",
  SORT: "sort",
};

/** Minimum unique area values required before showing the drawer section. */
export const MIN_HOTEL_AREA_FILTER_OPTIONS = 2;

/** Max display length for a city/resort filter label. */
const HOTEL_CITY_FILTER_MAX_LABEL_LENGTH = 40;

/** Fields checked in priority order (camelCase + PascalCase per group). */
const HOTEL_CITY_FILTER_FIELD_PRIORITY = [
  ["city", "City"],
  ["cityName", "CityName"],
  ["resort", "Resort"],
  ["resortName", "ResortName"],
  ["area", "Area"],
  ["region", "Region"],
  ["destinationName", "DestinationName", "destination", "Destination"],
  ["locality", "Locality"],
];

/** Normalize common spelling/transliteration variants → canonical display label. */
const HOTEL_CITY_CANONICAL_LABELS = Object.freeze({
  rhodes: "Rhodes",
  rodos: "Rhodes",
  "ρόδος": "Rhodes",
  lindos: "Lindos",
  "λίνδος": "Lindos",
  faliraki: "Faliraki",
  "φαληράκι": "Faliraki",
  pefki: "Pefkos",
  pefkos: "Pefkos",
  fanes: "Fanes",
});

const HOTEL_CITY_ADDRESS_TOKEN_PATTERN =
  /\b(road|rd\.?|street|str\.?|st\.?|avenue|ave\.?|boulevard|blvd|lane|ln|drive|dr\.?|beach)\b/i;
const HOTEL_CITY_UNKNOWN_PATTERN = /\b(unknown|unnamed|n\/a)\b/i;

const ALL_STARS = [1, 2, 3, 4, 5];

export const DEFAULT_HOTEL_FILTERS = Object.freeze({
  name: "",
  starRating: [...ALL_STARS],
  boardBasis: [],
  areas: [],
  pricePreset: HOTEL_PRICE_PRESET.ALL,
  priceMin: null,
  priceMax: null,
  sort: Object.freeze({
    field: HOTEL_SORT_FIELD.STARS,
    direction: HOTEL_SORT_DIRECTION.DESC,
  }),
});

const DEV =
  (typeof import.meta !== "undefined" && import.meta.env?.DEV) ||
  (typeof process !== "undefined" && process.env?.NODE_ENV === "development");

function emitDevTrace(tag, payload) {
  if (!DEV) return;
  try {
    console.info(tag, payload);
  } catch (_) {
    /* ignore */
  }
}

function finiteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function cloneDefaultSort() {
  return {
    field: DEFAULT_HOTEL_FILTERS.sort.field,
    direction: DEFAULT_HOTEL_FILTERS.sort.direction,
  };
}

/** Map legacy ViewFilter/HighLow or internal sort object → normalized sort. */
export function normalizeHotelSort(rawSort) {
  if (!rawSort || typeof rawSort !== "object") {
    return cloneDefaultSort();
  }
  if (rawSort.field && rawSort.direction) {
    const field =
      rawSort.field === HOTEL_SORT_FIELD.PRICE ? HOTEL_SORT_FIELD.PRICE : HOTEL_SORT_FIELD.STARS;
    const direction =
      rawSort.direction === HOTEL_SORT_DIRECTION.ASC
        ? HOTEL_SORT_DIRECTION.ASC
        : HOTEL_SORT_DIRECTION.DESC;
    return { field, direction };
  }
  const view = String(rawSort.ViewFilter || rawSort.viewFilter || "").toLowerCase();
  const highLow = Boolean(rawSort.HighLow ?? rawSort.highLow);
  if (view === "price") {
    return {
      field: HOTEL_SORT_FIELD.PRICE,
      direction: highLow ? HOTEL_SORT_DIRECTION.DESC : HOTEL_SORT_DIRECTION.ASC,
    };
  }
  return {
    field: HOTEL_SORT_FIELD.STARS,
    direction: highLow ? HOTEL_SORT_DIRECTION.DESC : HOTEL_SORT_DIRECTION.ASC,
  };
}

function normalizeStarRating(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return [...ALL_STARS];
  const parsed = raw
    .map((v) => parseInt(v, 10))
    .filter((n) => Number.isFinite(n) && n >= 1 && n <= 5);
  return parsed.length > 0 ? [...new Set(parsed)].sort((a, b) => a - b) : [...ALL_STARS];
}

function normalizeBoardBasis(raw) {
  const source = raw?.boardBasis ?? raw?.meals ?? raw?.basis ?? [];
  if (!Array.isArray(source)) return [];
  return [...new Set(source.map((v) => String(v).trim()).filter(Boolean))];
}

function normalizeAreaKeys(raw) {
  const source = raw?.areas;
  if (!Array.isArray(source)) return [];
  return [...new Set(source.map((v) => String(v).trim().toLowerCase()).filter(Boolean))];
}

/** Keep only area keys that exist in current facet options (ignore stale session values). */
export function sanitizeHotelAreaFilterKeys(selectedAreas, availableAreas = []) {
  const selected = normalizeAreaKeys({ areas: selectedAreas });
  if (selected.length === 0) return [];
  const validKeys = new Set(
    (Array.isArray(availableAreas) ? availableAreas : [])
      .map((entry) => String(entry?.key ?? "").trim().toLowerCase())
      .filter(Boolean)
  );
  if (validKeys.size === 0) return [];
  return selected.filter((key) => validKeys.has(key));
}

function pickHotelFieldValue(item, fieldKeys) {
  for (const key of fieldKeys) {
    const value = item?.[key];
    if (value != null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return "";
}

function isRejectedHotelCityFilterValue(raw) {
  const value = String(raw ?? "").trim();
  if (!value) return true;
  if (value.length > HOTEL_CITY_FILTER_MAX_LABEL_LENGTH) return true;
  if (/\d/.test(value)) return true;
  if (HOTEL_CITY_ADDRESS_TOKEN_PATTERN.test(value)) return true;
  if (HOTEL_CITY_UNKNOWN_PATTERN.test(value)) return true;
  if (value.split(/\s+/).filter(Boolean).length > 4) return true;
  return false;
}

function canonicalizeHotelCityFilterLabel(raw) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed || isRejectedHotelCityFilterValue(trimmed)) return null;
  const lookup = trimmed.toLowerCase();
  const label = HOTEL_CITY_CANONICAL_LABELS[lookup] ?? trimmed;
  if (isRejectedHotelCityFilterValue(label)) return null;
  return {
    key: label.toLowerCase(),
    label,
  };
}

/**
 * Resolve a single clean city/resort label for hotel city filter UI + matching.
 * Does not parse street addresses. Returns null when no trusted city field exists.
 */
export function resolveHotelCityFilterValue(hotel) {
  if (!hotel || typeof hotel !== "object") return null;
  for (const fieldKeys of HOTEL_CITY_FILTER_FIELD_PRIORITY) {
    const raw = pickHotelFieldValue(hotel, fieldKeys);
    if (!raw) continue;
    const canonical = canonicalizeHotelCityFilterLabel(raw);
    if (canonical) return canonical;
  }
  return null;
}

/** @deprecated Use resolveHotelCityFilterValue — kept for internal row prep. */
export function resolveHotelRowAreaCandidates(item) {
  const city = resolveHotelCityFilterValue(item);
  const labelsByKey = new Map();
  if (city) labelsByKey.set(city.key, city.label);
  return labelsByKey;
}

/** Resolve display label for an area key using facet metadata. */
export function resolveHotelAreaLabel(areaKey, availableAreas = []) {
  const key = String(areaKey ?? "").trim().toLowerCase();
  if (!key) return "";
  if (HOTEL_CITY_CANONICAL_LABELS[key]) return HOTEL_CITY_CANONICAL_LABELS[key];
  const match = (Array.isArray(availableAreas) ? availableAreas : []).find(
    (entry) => entry?.key === key
  );
  return match?.label || key;
}

function normalizePricePreset(raw) {
  const preset = String(raw?.pricePreset ?? raw?.price_preset ?? "").toLowerCase();
  if (Object.values(HOTEL_PRICE_PRESET).includes(preset)) {
    return preset;
  }
  return HOTEL_PRICE_PRESET.ALL;
}

/**
 * Normalize UI / legacy filter payload → canonical filter object.
 * @param {object|null|undefined} raw
 * @param {{ min?: number, max?: number }|null} [priceBounds]
 */
export function normalizeHotelFilters(raw, priceBounds = null) {
  const input = raw && typeof raw === "object" ? raw : {};
  const bounds = priceBounds || computeHotelPriceBounds([]);

  let pricePreset = normalizePricePreset(input);
  let priceMin = finiteNumber(input.priceMin ?? input.price_min);
  let priceMax = finiteNumber(input.priceMax ?? input.price_max);

  const legacyRange = input.priceRange;
  if (Array.isArray(legacyRange) && legacyRange.length === 2) {
    const rMin = finiteNumber(legacyRange[0]);
    const rMax = finiteNumber(legacyRange[1]);
    if (rMin != null && rMax != null) {
      const fullRange =
        Math.round(rMin) === Math.round(bounds.min) &&
        Math.round(rMax) === Math.round(bounds.max);
      if (fullRange) {
        pricePreset = HOTEL_PRICE_PRESET.ALL;
        priceMin = null;
        priceMax = null;
      } else {
        pricePreset = HOTEL_PRICE_PRESET.CUSTOM;
        priceMin = rMin;
        priceMax = rMax;
      }
    }
  }

  if (pricePreset === HOTEL_PRICE_PRESET.CUSTOM) {
    if (priceMin == null) priceMin = bounds.min;
    if (priceMax == null) priceMax = bounds.max;
    if (priceMin > priceMax) {
      const t = priceMin;
      priceMin = priceMax;
      priceMax = t;
    }
  } else {
    priceMin = null;
    priceMax = null;
  }

  return {
    name: String(input.name ?? input.hotelName ?? "").trim(),
    starRating: normalizeStarRating(input.starRating ?? input.stars),
    boardBasis: normalizeBoardBasis(input),
    areas: normalizeAreaKeys(input),
    pricePreset,
    priceMin,
    priceMax,
    sort: normalizeHotelSort(input.sort),
  };
}

/** Patch one filter key; preserves others. Emits external change trace in DEV. */
export function patchHotelFilters(currentFilters, patch, priceBounds = null, traceContext = {}) {
  const base = normalizeHotelFilters(currentFilters, priceBounds);
  const safePatch = patch && typeof patch === "object" ? patch : {};
  const merged = { ...base, ...safePatch };
  const normalized = normalizeHotelFilters(merged, priceBounds);
  const { changedFilterKey = null, changedFilterValue = null } = traceContext || {};
  emitDevTrace("[HOTEL_EXTERNAL_FILTER_CHANGE_TRACE]", {
    changedFilterKey,
    changedFilterValue,
    activeFilters: describeActiveHotelFilters(normalized, priceBounds),
    normalizedFilters: normalized,
  });
  return normalized;
}

/** Reset filters to defaults; optional bounds for drawer slider sync. */
export function resetHotelFilters(priceBounds = null) {
  const filters = normalizeHotelFilters(
    {
      ...DEFAULT_HOTEL_FILTERS,
      starRating: [...ALL_STARS],
      boardBasis: [],
      areas: [],
      sort: cloneDefaultSort(),
    },
    priceBounds
  );
  emitDevTrace("[HOTEL_FILTERS_RESET_TRACE]", {
    originalCount: null,
    filteredCount: null,
    activeFilters: [],
    priceBounds: priceBounds || null,
    filters,
  });
  return filters;
}

/**
 * Enrich raw hotel rows with stable filter fields (_filterPrice, _stars, …).
 * @param {object[]} hotels
 * @param {object} pricingContext
 */
export function prepareHotelFilterRows(hotels, pricingContext = {}) {
  const source = Array.isArray(hotels) ? hotels : [];
  const {
    resultsDatasetCurrency = null,
    displayCurrency = null,
    hotelHrCommissionPercentage = null,
    fixedHotelCommission = null,
    currencyRate = null,
    entitlementsStatus = null,
  } = pricingContext;

  return source.map((item) => {
    const displayName =
      item?.DisplayName ||
      item?.displayName ||
      item?.HotelName ||
      item?.hotelName ||
      item?.Name ||
      item?.name ||
      "";
    const address = item?.Address || item?.address || "";
    const apiPrice = agentHotelSearchLowestPackagePrice(item);
    const campaignDisplay = item?.isCampaignHotel
      ? resolveCampaignHotelSearchDisplayPrice(item, {
          displayCurrency,
          currencyRate,
        })
      : null;
    const priceVm = campaignDisplay
      ? null
      : computeAgentHotelSearchResultPriceVm({
          apiPrice,
          resultsDatasetCurrency,
          displayCurrency,
          hotelHrCommissionPercentage,
          fixedHotelCommission,
          currencyRate,
          entitlementsStatus,
        });
    const filterPrice =
      campaignDisplay?.amount != null && Number.isFinite(Number(campaignDisplay.amount))
        ? Number(campaignDisplay.amount)
        : priceVm?.finalPrice != null && Number.isFinite(Number(priceVm.finalPrice))
          ? Number(priceVm.finalPrice)
          : null;
    const parsedStar = parseInt(item?.StarRating, 10);
    const boards = Array.isArray(item?.boards) ? item.boards : [];
    const areaCandidatesMap = resolveHotelRowAreaCandidates(item);
    const areaCandidates = [...areaCandidatesMap.entries()].map(([key, label]) => ({
      key,
      label,
    }));
    const areaKeys = areaCandidates.map((entry) => entry.key);
    return {
      ...item,
      _filterPrice: filterPrice,
      _sortDisplayPrice: filterPrice,
      _finalPriceILS: filterPrice,
      _stars: Number.isFinite(parsedStar) ? parsedStar : null,
      _board: boards.map((board) => String(board).split(" ").pop()).filter(Boolean),
      _city: String(item?.City || item?.city || "").toLowerCase(),
      _areaKeys: areaKeys,
      _areaCandidates: areaCandidates,
      _areaLabel: areaCandidates[0]?.label ?? "",
      _hotelDisplayNameNormalized: String(displayName).toLowerCase().trim(),
      _hotelNameNormalized: `${String(displayName).toLowerCase()} ${String(address).toLowerCase()}`.trim(),
    };
  });
}

/** Min/max display prices from prepared rows. */
export function computeHotelPriceBounds(rows) {
  const prices = (Array.isArray(rows) ? rows : [])
    .map((row) => row?._filterPrice)
    .filter((p) => Number.isFinite(p));
  if (!prices.length) {
    return { min: 0, max: 0, count: 0 };
  }
  return {
    min: Math.min(...prices),
    max: Math.max(...prices),
    count: prices.length,
  };
}

/** Available facet values from original prepared rows. */
export function computeHotelFilterFacets(rows) {
  const source = Array.isArray(rows) ? rows : [];
  const stars = new Set();
  const boards = new Set();
  const areaCounts = new Map();
  for (const row of source) {
    if (Number.isFinite(row?._stars)) stars.add(row._stars);
    if (Array.isArray(row?._board)) {
      row._board.forEach((code) => boards.add(code));
    }
    const seenAreaKeys = new Set();
    for (const entry of row?._areaCandidates ?? []) {
      const key = entry?.key;
      if (!key || seenAreaKeys.has(key)) continue;
      seenAreaKeys.add(key);
      const prev = areaCounts.get(key) || {
        key,
        label: entry?.label || key,
        count: 0,
      };
      prev.count += 1;
      areaCounts.set(key, prev);
    }
  }
  const availableAreas = [...areaCounts.values()].sort((a, b) =>
    String(a.label).localeCompare(String(b.label), "he", { sensitivity: "base" })
  );
  return {
    availableStars: [...stars].sort((a, b) => a - b),
    availableBoards: [...boards].sort(),
    availableAreas,
    availablePriceRange: computeHotelPriceBounds(source),
  };
}

/** Resolve numeric [min, max] range from preset + bounds. */
export function resolveHotelPriceRange(filters, bounds) {
  const bMin = finiteNumber(bounds?.min) ?? 0;
  const bMax = finiteNumber(bounds?.max) ?? bMin;
  if (bMax <= bMin && bMin === 0 && bMax === 0) {
    return { min: 0, max: Number.MAX_SAFE_INTEGER, preset: filters.pricePreset };
  }
  const span = Math.max(bMax - bMin, 1);
  switch (filters.pricePreset) {
    case HOTEL_PRICE_PRESET.LOW:
      return { min: bMin, max: bMin + span * 0.33, preset: HOTEL_PRICE_PRESET.LOW };
    case HOTEL_PRICE_PRESET.MID:
      return {
        min: bMin + span * 0.33,
        max: bMin + span * 0.66,
        preset: HOTEL_PRICE_PRESET.MID,
      };
    case HOTEL_PRICE_PRESET.HIGH:
      return { min: bMin + span * 0.66, max: bMax, preset: HOTEL_PRICE_PRESET.HIGH };
    case HOTEL_PRICE_PRESET.CUSTOM:
      return {
        min: finiteNumber(filters.priceMin) ?? bMin,
        max: finiteNumber(filters.priceMax) ?? bMax,
        preset: HOTEL_PRICE_PRESET.CUSTOM,
      };
    case HOTEL_PRICE_PRESET.ALL:
    default:
      return { min: bMin, max: bMax, preset: HOTEL_PRICE_PRESET.ALL };
  }
}

function expandBoardBasisCodes(codes) {
  const set = new Set(codes);
  if (set.has("HB") && !set.has("CB")) set.add("CB");
  if (set.has("CB") && !set.has("HB")) set.delete("CB");
  return [...set];
}

function rowPassesStarFilter(row, starRating) {
  if (!starRating || starRating.length === 0 || starRating.length >= 5) return true;
  const sr = row?.StarRating;
  if (sr === "-1" || sr === -1 || sr === "" || sr == null) return true;
  if (!Number.isFinite(row?._stars)) return true;
  return starRating.includes(row._stars);
}

function rowPassesNameFilter(row, name) {
  if (!name) return true;
  const term = name.toLowerCase();
  const haystack =
    row?._hotelDisplayNameNormalized ?? row?._hotelNameNormalized ?? "";
  return Boolean(haystack.includes(term));
}

function rowPassesPriceFilter(row, range) {
  if (!range || range.preset === HOTEL_PRICE_PRESET.ALL) return true;
  const p = row?._filterPrice;
  if (!Number.isFinite(p)) return true;
  return p >= range.min && p <= range.max;
}

function rowPassesBoardFilter(row, boardBasis) {
  if (!boardBasis || boardBasis.length === 0) return true;
  const expanded = expandBoardBasisCodes(boardBasis);
  const boards = row?._board;
  if (!Array.isArray(boards) || boards.length === 0) {
    // Board data not loaded yet — do not hide hotel (avoid empty false negatives).
    return true;
  }
  return boards.some((code) => expanded.includes(code));
}

function rowPassesAreaFilter(row, selectedAreas) {
  if (!Array.isArray(selectedAreas) || selectedAreas.length === 0) return true;
  const rowKeys = row?._areaKeys;
  if (!Array.isArray(rowKeys) || rowKeys.length === 0) return false;
  return rowKeys.some((key) => selectedAreas.includes(key));
}

function sortHotelRows(rows, sort) {
  const list = [...rows];
  const pinRank = (row) =>
    row?.isCampaignHotel === true || row?.isPinnedByHotelsRiver === true || row?.isPinned === true
      ? 0
      : 1;
  const tieBreak = (a, b) => (Number(a?._hr_sort_index) || 0) - (Number(b?._hr_sort_index) || 0);
  const priceOf = (row) =>
    Number.isFinite(row?._filterPrice) ? row._filterPrice : Number.POSITIVE_INFINITY;

  if (sort.field === HOTEL_SORT_FIELD.PRICE) {
    const desc = sort.direction === HOTEL_SORT_DIRECTION.DESC;
    list.sort((a, b) => {
      const byPin = pinRank(a) - pinRank(b);
      if (byPin !== 0) return byPin;
      const byPrice = desc ? priceOf(b) - priceOf(a) : priceOf(a) - priceOf(b);
      return byPrice !== 0 ? byPrice : tieBreak(a, b);
    });
    return list;
  }

  const desc = sort.direction === HOTEL_SORT_DIRECTION.DESC;
  list.sort((a, b) => {
    const byPin = pinRank(a) - pinRank(b);
    if (byPin !== 0) return byPin;
    const sa = a?._stars || 0;
    const sb = b?._stars || 0;
    const byStars = desc ? sb - sa : sa - sb;
    return byStars !== 0 ? byStars : tieBreak(a, b);
  });
  return list;
}

export function describeActiveHotelFilters(filters, priceBounds) {
  const normalized = normalizeHotelFilters(filters, priceBounds);
  const active = [];
  if (normalized.name) active.push({ key: "name", value: normalized.name });
  if (
    normalized.starRating.length > 0 &&
    normalized.starRating.length < ALL_STARS.length
  ) {
    active.push({ key: "starRating", value: normalized.starRating });
  }
  if (normalized.boardBasis.length > 0) {
    active.push({ key: "boardBasis", value: normalized.boardBasis });
  }
  if (normalized.areas.length > 0) {
    active.push({ key: "areas", value: normalized.areas });
  }
  if (normalized.pricePreset !== HOTEL_PRICE_PRESET.ALL) {
    active.push({
      key: "pricePreset",
      value: normalized.pricePreset,
      min: normalized.priceMin,
      max: normalized.priceMax,
    });
  }
  return active;
}

/**
 * Apply all hotel filters on prepared rows (SSOT). Always starts from originalHotels.
 * Filter phase runs first; sort runs after filter phase.
 * @param {object[]} originalHotels — output of prepareHotelFilterRows
 * @param {object} activeFilters — raw or normalized
 * @param {{ priceBounds?: object, trace?: boolean, changedFilterKey?: string, changedFilterValue?: unknown }} [options]
 * @returns {{ hotels: object[], meta: object }}
 */
export function applyHotelFilters(originalHotels, activeFilters, options = {}) {
  const source = Array.isArray(originalHotels) ? originalHotels : [];
  const priceBounds = options.priceBounds || computeHotelPriceBounds(source);
  const facets = computeHotelFilterFacets(source);
  const normalized = normalizeHotelFilters(activeFilters, priceBounds);
  const sanitizedAreas = sanitizeHotelAreaFilterKeys(normalized.areas, facets.availableAreas);
  const filtersForApply =
    sanitizedAreas.length === normalized.areas.length &&
    sanitizedAreas.every((key, index) => key === normalized.areas[index])
      ? normalized
      : { ...normalized, areas: sanitizedAreas };
  const priceRange = resolveHotelPriceRange(filtersForApply, priceBounds);
  const originalCount = source.length;

  let filtered = source.filter((row) => {
    const sr = row?._stars;
    const originalSr = row?.StarRating;
    if (originalSr === "-1" || originalSr === -1 || originalSr === "" || originalSr == null) {
      return true;
    }
    if (!Number.isFinite(sr)) return true;
    return sr >= 1 && sr <= 5;
  });

  filtered = filtered.filter(
    (row) =>
      rowPassesNameFilter(row, filtersForApply.name) &&
      rowPassesStarFilter(row, filtersForApply.starRating) &&
      rowPassesPriceFilter(row, priceRange) &&
      rowPassesBoardFilter(row, filtersForApply.boardBasis) &&
      rowPassesAreaFilter(row, filtersForApply.areas)
  );

  const filteredCount = filtered.length;
  const sorted = sortHotelRows(filtered, filtersForApply.sort);

  let reasonIfEmpty = null;
  if (originalCount > 0 && sorted.length === 0) {
    if (filtersForApply.name) reasonIfEmpty = "name_no_match";
    else if (filtersForApply.areas.length > 0) reasonIfEmpty = "area_no_match";
    else if (filtersForApply.boardBasis.length > 0) reasonIfEmpty = "board_no_match";
    else if (filtersForApply.pricePreset !== HOTEL_PRICE_PRESET.ALL) {
      reasonIfEmpty = "price_range_no_match";
    } else if (
      filtersForApply.starRating.length > 0 &&
      filtersForApply.starRating.length < ALL_STARS.length
    ) {
      reasonIfEmpty = "star_rating_no_match";
    } else {
      reasonIfEmpty = "combined_filters_no_match";
    }
  }

  const sortMode = `${filtersForApply.sort.field}_${filtersForApply.sort.direction}`;

  const meta = {
    originalCount,
    filteredCount: sorted.length,
    totalHotelsBefore: originalCount,
    totalHotelsAfter: sorted.length,
    activeFilters: describeActiveHotelFilters(filtersForApply, priceBounds),
    changedFilterKey: options.changedFilterKey ?? null,
    changedFilterValue: options.changedFilterValue ?? null,
    availablePriceRange: facets.availablePriceRange,
    availableStars: facets.availableStars,
    availableBoards: facets.availableBoards,
    availableAreas: facets.availableAreas,
    sortMode,
    selectedPriceFilter: filtersForApply.pricePreset,
    resolvedPriceRange: priceRange,
    reasonIfEmpty,
    reasonIfNoResults: reasonIfEmpty,
    normalizedFilters: filtersForApply,
  };

  if (options.trace !== false) {
    emitDevTrace("[HOTEL_FILTERS_APPLY_TRACE]", meta);
    if (
      normalized.sort.field !== HOTEL_SORT_FIELD.STARS ||
      normalized.sort.direction !== HOTEL_SORT_DIRECTION.DESC
    ) {
      emitDevTrace("[HOTEL_SORT_APPLY_TRACE]", {
        sortMode,
        originalCount,
        filteredCount: sorted.length,
        firstIds: sorted.slice(0, 5).map((h) => h?.ID ?? h?.Id),
      });
    }
    if (normalized.pricePreset !== HOTEL_PRICE_PRESET.ALL) {
      emitDevTrace("[HOTEL_PRICE_FILTER_TRACE]", {
        selectedPriceFilter: normalized.pricePreset,
        resolvedPriceRange: priceRange,
        availablePriceRange: facets.availablePriceRange,
        originalCount,
        filteredCount: sorted.length,
        reasonIfEmpty,
      });
    }
  }

  return { hotels: sorted, meta };
}

/** Count non-default filters for badge UI. */
export function countActiveHotelFilters(filters, priceBounds) {
  return describeActiveHotelFilters(filters, priceBounds).length;
}

/** Quick-bar / UI: detect which price preset matches current filters. */
export function detectHotelPricePresetFromFilters(filters, priceBounds) {
  return normalizeHotelFilters(filters, priceBounds).pricePreset;
}

/** Map legacy dropdown / preset strings → canonical price sort filter value. */
export function normalizeHotelPriceSortFilterValue(rawValue) {
  const v = String(rawValue ?? "")
    .trim()
    .toLowerCase();
  if (
    v === HOTEL_PRICE_SORT_FILTER.LOW_TO_HIGH ||
    v === "price_asc" ||
    v === "pricelowtohigh" ||
    v === HOTEL_PRICE_PRESET.LOW
  ) {
    return HOTEL_PRICE_SORT_FILTER.LOW_TO_HIGH;
  }
  if (
    v === HOTEL_PRICE_SORT_FILTER.HIGH_TO_LOW ||
    v === "price_desc" ||
    v === "pricehightolow" ||
    v === HOTEL_PRICE_PRESET.HIGH
  ) {
    return HOTEL_PRICE_SORT_FILTER.HIGH_TO_LOW;
  }
  if (
    v === HOTEL_PRICE_SORT_FILTER.ALL ||
    v === HOTEL_PRICE_PRESET.ALL ||
    v === HOTEL_PRICE_PRESET.MID ||
    v === HOTEL_PRICE_PRESET.CUSTOM ||
    v === ""
  ) {
    return HOTEL_PRICE_SORT_FILTER.ALL;
  }
  return HOTEL_PRICE_SORT_FILTER.ALL;
}

/** Read active price sort filter from canonical sort state. */
export function detectHotelPriceSortFilterFromFilters(filters) {
  const sort = normalizeHotelSort(filters?.sort);
  if (sort.field !== HOTEL_SORT_FIELD.PRICE) {
    return HOTEL_PRICE_SORT_FILTER.ALL;
  }
  return sort.direction === HOTEL_SORT_DIRECTION.ASC
    ? HOTEL_PRICE_SORT_FILTER.LOW_TO_HIGH
    : HOTEL_PRICE_SORT_FILTER.HIGH_TO_LOW;
}

/** Canonical price sort filter → normalized sort object (same as side-modal radios). */
export function hotelPriceSortFilterToSort(rawValue) {
  const value = normalizeHotelPriceSortFilterValue(rawValue);
  switch (value) {
    case HOTEL_PRICE_SORT_FILTER.LOW_TO_HIGH:
      return {
        field: HOTEL_SORT_FIELD.PRICE,
        direction: HOTEL_SORT_DIRECTION.ASC,
      };
    case HOTEL_PRICE_SORT_FILTER.HIGH_TO_LOW:
      return {
        field: HOTEL_SORT_FIELD.PRICE,
        direction: HOTEL_SORT_DIRECTION.DESC,
      };
    case HOTEL_PRICE_SORT_FILTER.ALL:
    default:
      return cloneDefaultSort();
  }
}

/** Slider UI range [min, max] derived from applied filters + dataset bounds. */
export function resolveHotelPriceSliderRange(filters, priceBounds) {
  const normalized = normalizeHotelFilters(filters, priceBounds);
  const range = resolveHotelPriceRange(normalized, priceBounds);
  return [range.min, range.max];
}

/** DEV-only trace for hotel price filter SSOT (side modal ↔ top bar). */
export function traceHotelPriceFilterSsot(payload = {}) {
  emitDevTrace("[HOTEL_PRICE_FILTER_SSOT_TRACE]", payload);
}

/** DEV-only trace for hotel price sort filter (top dropdown ↔ side-modal radios). */
export function traceHotelPriceSortFilter(payload = {}) {
  emitDevTrace("[HOTEL_PRICE_SORT_FILTER_TRACE]", payload);
}

/** Legacy sort shape for drawer radio controls. */
export function hotelSortToLegacyViewFilter(sort) {
  const normalized = normalizeHotelSort(sort);
  if (normalized.field === HOTEL_SORT_FIELD.PRICE) {
    return {
      ViewFilter: "Price",
      HighLow: normalized.direction === HOTEL_SORT_DIRECTION.DESC,
    };
  }
  return {
    ViewFilter: "StarRating",
    HighLow: normalized.direction === HOTEL_SORT_DIRECTION.DESC,
  };
}
